import { useMemo, useState } from 'react'
import { Download, Loader2, RotateCcw, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import { tarjetaDe } from './estados'

/**
 * QUÉ DATOS TRAE EL CLIENTE, Y CUÁLES FALTAN — por tipo de dato.
 *
 * El asistente preguntaba «suelte sus archivos» sin decir cuáles. El asesor no sabía qué pedirle
 * al cliente, y el cliente mandaba lo que exportara su sistema viejo. Aquí se ve de un vistazo qué
 * tipo de dato ya está en la mesa, cuál tiene errores y cuál falta, y cada uno trae su PLANTILLA:
 * un Excel generado de lo que la empresa acepta, que al volver lleno se reconoce solo.
 *
 * En el paso de carga cada tarjeta es además UNA carga en la empresa: se carga sola y se deshace
 * sola. Antes la migración entera era una carga, y corregir los clientes obligaba a borrar
 * también el catálogo que había entrado bien.
 *
 * Subir sigue siendo la bandeja: una sola puerta para archivos propios y plantillas.
 */

const GRUPOS = [
  { clave: 'maestros', titulo: 'Catálogos' },
  { clave: 'personas', titulo: 'Personas' },
  { clave: 'comercial', titulo: 'Comercial' },
  { clave: 'saldos', titulo: 'Saldos iniciales' },
  { clave: 'historial', titulo: 'Historial', nota: 'Opcional: para consultar, no mueve saldos.' },
]

/** Estado de un tipo de dato según las hojas del proyecto que ya dicen serlo (con sus líneas). */
// eslint-disable-next-line react-refresh/only-export-components -- la usan las pruebas y el tablero
export function estadoDeEntidad(entidad, hojas, especificaciones) {
  const suyas = (hojas || []).filter(
    (h) => h.entidad && tarjetaDe(h.entidad, especificaciones) === entidad && h.estado !== 'omitida'
  )
  if (suyas.length === 0) return { clave: 'falta', etiqueta: 'Sin archivo', variante: 'outline' }
  const cargadas = suyas.filter((h) => h.estado === 'cargada')
  const conErrores = suyas.some((h) => (h.carga?.errores || 0) > 0 || (h.estado !== 'cargada' && (h.ensayo?.errores || 0) > 0))
  if (cargadas.length === suyas.length) {
    const escritas = cargadas.reduce((n, h) => n + (h.carga?.escritas || 0), 0)
    return conErrores
      ? { clave: 'cargado-con-errores', etiqueta: `Cargado · ${escritas} · con filas fuera`, variante: 'warning' }
      : { clave: 'cargado', etiqueta: `Cargado · ${escritas}`, variante: 'success' }
  }
  if (conErrores) return { clave: 'errores', etiqueta: 'Con errores', variante: 'danger' }
  const filas = suyas.reduce((n, h) => n + (h.totalFilas || 0), 0)
  return { clave: 'revision', etiqueta: `En revisión · ${filas} filas`, variante: 'warning' }
}

export default function TableroDatos({
  especificaciones,
  orden,
  hojas,
  cargas = [],
  progreso,
  onPlantilla,
  // Sin estos dos, el tablero solo informa (paso de recopilar): no se escribe desde ahí.
  onCargar,
  onDeshacer,
  bloqueosDe,
}) {
  const [bajando, setBajando] = useState(null)
  const [preparando, setPreparando] = useState(null)
  const [confirmar, setConfirmar] = useState(null)

  const porGrupo = useMemo(() => {
    const specs = especificaciones || {}
    // El orden de carga, cuando se conoce; si no, el de la especificación.
    const claves = (orden?.length ? orden : Object.keys(specs)).filter((k) => specs[k])
    // Las hojas de LÍNEAS van dentro de la tarjeta de su cabecera: no son un tipo aparte.
    const visibles = claves.filter((k) => !specs[k].hijaDe)
    return GRUPOS.map((g) => ({
      ...g,
      entidades: visibles.filter((k) => (specs[k].grupo || 'maestros') === g.clave).map((k) => specs[k]),
    })).filter((g) => g.entidades.length > 0)
  }, [especificaciones, orden])

  if (!porGrupo.length) return null

  const enMarcha = progreso?.fase === 'cargando' || progreso?.fase === 'cerrando'

  const bajar = async (entidad) => {
    setBajando(entidad)
    try {
      await onPlantilla(entidad)
    } finally {
      setBajando(null)
    }
  }

  // Primero se cuenta, luego se confirma: el asesor decide viendo cuántas filas se van.
  const pedirDeshacer = async (spec) => {
    setPreparando(spec.entidad)
    try {
      const r = await onDeshacer(spec.entidad, { previsualizar: true })
      if (r) setConfirmar({ spec, previa: r.previsualizacion || null })
    } finally {
      setPreparando(null)
    }
  }

  const descripcionDeshacer = (c) => {
    if (!c) return ''
    const total = c.previa?.total
    const enUso = c.previa?.bloqueos?.length || 0
    const cuanto = typeof total === 'number' ? `${total.toLocaleString('es-SV')} filas` : 'todo lo que entró'
    return (
      `Se borrarán de la empresa ${cuanto} de «${c.spec.etiqueta}», con su asiento de apertura si lo tuvo. ` +
      'Los archivos y el mapeo se quedan: puedes corregir y volver a cargar.' +
      (enUso > 0 ? ` Ojo: ${enUso} dato(s) ya los usó el cliente y la empresa no dejará borrarlos.` : '')
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-body font-semibold text-neutral-900 dark:text-neutral-50">Qué datos trae el cliente</h3>
        <p className="text-body-sm text-neutral-600 dark:text-neutral-400">
          {onCargar
            ? 'Cada tipo de dato se carga y se deshace por separado. Los que dependen de otro esperan a que ese entre.'
            : 'Descargá la plantilla de lo que falte y mandásela al cliente. Llena, se sube abajo como cualquier archivo y se reconoce sola.'}
        </p>
      </div>
      {porGrupo.map((g) => (
        <section key={g.clave} className="space-y-2">
          <div className="flex flex-wrap items-baseline gap-2">
            <h4 className="text-caption font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">{g.titulo}</h4>
            {g.nota && <span className="text-caption text-neutral-500 dark:text-neutral-400">{g.nota}</span>}
          </div>
          <ul className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {g.entidades.map((spec) => {
              const e = spec.entidad
              const estado = estadoDeEntidad(e, hojas, especificaciones)
              const antes = (spec.dependeDe || []).map((d) => especificaciones?.[d]?.etiqueta || d)
              const cargaActiva = [...cargas].reverse().find((c) => c.entidad === e && c.estado === 'cerrada')
              const pendiente = (hojas || []).some(
                (h) => h.entidad && tarjetaDe(h.entidad, especificaciones) === e && h.estado !== 'omitida' && h.estado !== 'cargada'
              )
              const estaCargando = enMarcha && progreso?.entidad === e
              const bloqueos = onCargar && pendiente ? bloqueosDe?.(e) || [] : []
              return (
                <li
                  key={e}
                  className="flex flex-col gap-2 rounded-md border border-neutral-200 dark:border-neutral-800 px-3 py-2"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-body-sm font-medium text-neutral-900 dark:text-neutral-50">{spec.etiqueta}</span>
                    <Badge variant={estaCargando ? 'live' : estado.variante} className="shrink-0">
                      {estaCargando ? 'Cargando…' : estado.etiqueta}
                    </Badge>
                  </div>
                  {antes.length > 0 && (
                    <span className="text-caption text-neutral-500 dark:text-neutral-400">Después de: {antes.join(', ')}</span>
                  )}
                  {bloqueos.length > 0 && (
                    <span className="text-caption text-warning-700 dark:text-warning-300">{bloqueos[0].motivo}</span>
                  )}
                  <div className="flex flex-wrap items-center gap-1">
                    <Button
                      variant="ghost"
                      size="xs"
                      disabled={bajando === e}
                      onClick={() => bajar(e)}
                    >
                      {bajando === e ? (
                        <Loader2 size={13} className="mr-1 animate-spin" />
                      ) : (
                        <Download size={13} className="mr-1" />
                      )}
                      Plantilla
                    </Button>
                    {onCargar && pendiente && (
                      <Button
                        variant="outline"
                        size="xs"
                        disabled={enMarcha || bloqueos.length > 0}
                        onClick={() => onCargar(e)}
                      >
                        <Upload size={13} className="mr-1" />
                        {cargaActiva ? 'Cargar lo que falta' : 'Cargar'}
                      </Button>
                    )}
                    {onDeshacer && cargaActiva && (
                      <Button
                        variant="ghost"
                        size="xs"
                        disabled={enMarcha || preparando === e}
                        onClick={() => pedirDeshacer(spec)}
                      >
                        {preparando === e ? (
                          <Loader2 size={13} className="mr-1 animate-spin" />
                        ) : (
                          <RotateCcw size={13} className="mr-1" />
                        )}
                        Deshacer
                      </Button>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        </section>
      ))}

      <ConfirmDialog
        abierto={Boolean(confirmar)}
        onCerrar={() => setConfirmar(null)}
        onConfirmar={() => {
          const e = confirmar?.spec?.entidad
          setConfirmar(null)
          if (e) onDeshacer(e)
        }}
        variante="destructiva"
        titulo={confirmar ? `¿Deshacer la carga de ${confirmar.spec.etiqueta}?` : ''}
        descripcion={descripcionDeshacer(confirmar)}
        textoConfirmar="Deshacer la carga"
      />
    </div>
  )
}
